import { useState, useEffect, useRef, useContext } from "react";
import {
  Camera, Bell, LayoutDashboard, FileText,
  ShieldCheck, Users, TrendingUp, AlertTriangle,
  CheckCircle2, XCircle, Activity,
  Package, HardHat, Zap, Map, ChevronRight,
  Menu, X, ArrowUpRight, ArrowDownRight,
  Wifi, WifiOff, Video, VideoOff, Star, Clock, Eye, Coffee,
  DollarSign,
  AlertOctagon,
  Cpu,
  ShieldAlert,
  Thermometer,
  UserCheck,
  Wrench,
  Layers,
  Flame,
  Settings,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { RootContext } from "../../../context/RootContext";

// ─── PAGE META ────────────────────────────────────────────────────────────────
const PAGE_META = {
  dashboard:  { title: "Dashboard",            icon: <LayoutDashboard size={18}/>, description: "Overview of all operations" },
  camera:     { title: "Camera Monitor",        icon: <Camera size={18}/>,          description: "Live camera feeds & status" },
  alert:      { title: "Alerts",               icon: <Bell size={18}/>,             description: "Real-time system alerts" },
  report:     { title: "Reports",              icon: <FileText size={18}/>,          description: "Analytics & scheduled reports" },
  safety:     { title: "Safety",               icon: <ShieldCheck size={18}/>,       description: "PPE & safety compliance" },
  heatmap:    { title: "Heatmap",              icon: <Map size={18}/>,               description: "Zone activity heatmaps" },
  people:     { title: "People Analytics",     icon: <Users size={18}/>,             description: "Staff & visitor tracking" },
  inventory:  { title: "Inventory",            icon: <Package size={18}/>,           description: "Stock & shelf monitoring" },
  production: { title: "Production",           icon: <Activity size={18}/>,          description: "Line productivity & defects" },
};

const USE_CASE_KEY_MAP = {
  "canteen monitoring": "Canteen monitoring",
  "safety monitoring": "Safety Monitoring",
  "employee productivity": "Employee Productivity",
  "food safety hygiene": "Food Safety & Hygiene",
  "customer wait times": "Customer Wait Times",
  "staff ppe compliance": "Staff PPE Compliance",
  "inventory wastage": "Inventory & Wastage",
  "shift scheduling": "Shift Scheduling",
  "cash pos reconciliation": "Cash & POS Reconciliation",
  "parking occupancy monitoring": "Parking Occupancy Monitoring",
  "vehicle counting":             "Vehicle Counting",
  "illegal parking detection":    "Illegal Parking Detection",
  "parking duration tracking":    "Parking Duration Tracking",
};

const USE_CASE_PAGES = {
  "Canteen monitoring":           ["dashboard","camera","alert","people","heatmap"],
  "Employee Productivity":        ["dashboard","people","report"],
  "Food Safety & Hygiene":        ["dashboard","alert","camera","safety"],
  "Customer Wait Times":          ["dashboard","alert","report","heatmap","camera"],
  "Staff PPE Compliance":         ["dashboard","safety","alert","camera"],
  "Inventory & Wastage":          ["dashboard","inventory","report","camera"],
  "Shift Scheduling":             ["dashboard","people","heatmap","report","camera"],
  "Cash & POS Reconciliation":    ["dashboard","alert","report","camera"],
  "PPE Compliance":               ["dashboard","safety","alert","camera"],
  "Line Productivity Monitoring": ["dashboard","production","report","camera"],
  "Quality Defect Detection":     ["dashboard","production","alert","camera"],
  "Unauthorised Zone Access":     ["dashboard","alert","camera"],
  "Equipment Anomaly Detection":  ["dashboard","alert","camera","production"],
  "Ergonomics & Fatigue":         ["dashboard","people","alert","camera"],
  "Visitor & Contractor Management":["dashboard","camera","alert","people"],
  "Footfall & Heatmapping":       ["dashboard","heatmap","report","camera"],
  "Queue Management":             ["dashboard","alert","camera","report"],
  "Theft & Loss Prevention":      ["dashboard","camera","alert"],
  "Planogram Compliance":         ["dashboard","inventory","camera"],
  "Staff Deployment":             ["dashboard","people","heatmap","camera"],
  "Safety Monitoring":            ["dashboard","alert","camera","report"],
  "Parking Occupancy Monitoring":  ["dashboard", "camera", "alert", "report", "heatmap"],
  "Vehicle Counting":              ["dashboard", "camera", "alert", "report"],
  "Illegal Parking Detection":     ["dashboard", "camera", "alert", "report"],
  "Parking Duration Tracking":     ["dashboard", "camera", "alert", "report"],
};

const USE_CASE_WIDGETS = {
  "Safety Monitoring": {
    dashboard: [
      { id:"sm-incidents", title:"Total Incidents Today", type:"stat", value:"3", delta:"+1", up:false, icon:<ShieldAlert size={16}/>, color:"#ef4444" },
      { id:"sm-falls", title:"Falls Detected", type:"stat", value:"2", delta:"+1", up:false, icon:<ShieldAlert size={16}/>, color:"#f97316" },
      { id:"sm-fights", title:"Fights Detected", type:"stat", value:"1", delta:"Alert", up:false, icon:<AlertOctagon size={16}/>, color:"#ef4444" },
      { id:"sm-response", title:"Avg Response Time", type:"stat", value:"1.1m", delta:"-0.2m", up:true, icon:<Clock size={16}/>, color:"#22c55e" },
      { id:"sm-score", title:"Overall Safety Score", type:"gauge", value:79, color:"#f59e0b" },
      { id:"sm-trend", title:"Incidents Trend (Fall + Fight)", type:"chart", color:"#ef4444" },
      { id:"sm-zone", title:"Zone-wise Safety Incidents", type:"chart", color:"#f97316" },
      { id:"sm-log", title:"Safety Incident Log", type:"list" },
    ],
    alert: [
      { id:"al-sm1", title:"Person Fall Detected — Zone B", severity:"high", camera:"CAM-02", time:"02:15 PM", status:"Open" },
      { id:"al-sm2", title:"Fight Detected — Queue Area", severity:"high", camera:"CAM-01", time:"01:55 PM", status:"Open" },
      { id:"al-sm3", title:"Aggressive Behaviour — Entrance", severity:"medium", camera:"CAM-02", time:"01:20 PM", status:"Unresolved" },
      { id:"al-sm4", title:"Fall Event Cleared — No Injury", severity:"low", camera:"CAM-01", time:"11:45 AM", status:"Resolved" },
    ],
    camera: [
      { id:"cam-01", title:"CAM-01 · Main Floor", type:"feed", status:"online", health:"Good", last:"just now" },
      { id:"cam-02", title:"CAM-02 · Entrance / Queue", type:"feed", status:"online", health:"Good", last:"1 min ago" },
      { id:"cam-03", title:"CAM-03 · Stairwell / Risk Zone", type:"feed", status:"online", health:"Average", last:"3 min ago" },
      { id:"cam-04", title:"CAM-04 · Parking / Low Visibility", type:"feed", status:"offline", health:"Critical", last:"45 min ago" },
    ],
    report: [
      { id:"rp-sm1", title:"Daily Safety Incident Summary", type:"chart", color:"#ef4444", period:"Today" },
      { id:"rp-sm2", title:"Weekly Incident Trend", type:"chart", color:"#f97316", period:"This Week" },
      { id:"rp-sm3", title:"Safety Incident Report", type:"table" },
    ],
  },

  "Canteen monitoring": {
    dashboard: [
      { id:"cm-occ",   title:"Table Occupancy",      type:"stat",  value:"14/20",  delta:"70%",   up:true,  icon:<Coffee size={16}/>,       color:"#f97316" },
      { id:"cm-count", title:"People in Canteen",    type:"stat",  value:"38",     delta:"+5",    up:true,  icon:<Users size={16}/>,         color:"#6366f1" },
      { id:"cm-phone", title:"Phone Violations",     type:"stat",  value:"2",      delta:"-1",    up:true,  icon:<AlertTriangle size={16}/>, color:"#ef4444" },
      { id:"cm-fall",  title:"Fall/Fight Incidents", type:"stat",  value:"0",      delta:"Clear", up:null,  icon:<ShieldCheck size={16}/>,   color:"#22c55e" },
      { id:"cm-trend", title:"Hourly Canteen Traffic",type:"chart", color:"#f97316" },
      { id:"cm-occ2",  title:"Occupancy Over Time",  type:"chart", color:"#6366f1" },
      { id:"cm-events",title:"Recent Canteen Events",type:"list" },
      { id:"cm-gauge", title:"Canteen Utilisation",  type:"gauge", value:70, color:"#f97316" },
    ],
    camera:[
      { id:"cam-01",title:"CAM-01 · Canteen Entrance",type:"feed",status:"online", health:"Good",   last:"just now" },
      { id:"cam-02",title:"CAM-02 · Seating Zone A",  type:"feed",status:"online", health:"Good",   last:"2 min ago" },
      { id:"cam-03",title:"CAM-03 · Seating Zone B",  type:"feed",status:"online", health:"Average",last:"1 min ago" },
      { id:"cam-04",title:"CAM-04 · Counter Area",    type:"feed",status:"offline",health:"Critical",last:"30 min ago" },
    ],
    alert:[
      { id:"al-01",title:"Staff Mobile Phone Detected",  severity:"medium",camera:"CAM-02",time:"11:22 AM",status:"Open" },
      { id:"al-02",title:"Canteen Overcrowded (>40pax)", severity:"high",  camera:"CAM-01",time:"12:05 PM",status:"Open" },
      { id:"al-03",title:"Table Occupancy at 90%",       severity:"low",   camera:"CAM-03",time:"11:50 AM",status:"Resolved" },
    ],
    people:[
      { id:"ppl-staff",title:"Staff in Canteen", type:"stat",  value:"6",  delta:"+1",up:true, icon:<Users size={16}/>, color:"#6366f1" },
      { id:"ppl-avg",  title:"Avg Dwell Time",   type:"stat",  value:"18m",delta:"+2m",up:false,icon:<Clock size={16}/>, color:"#f59e0b" },
      { id:"ppl-chart",title:"Footfall Timeline",type:"chart", color:"#6366f1" },
      { id:"ppl-list", title:"Movement Log",     type:"list" },
    ],
    heatmap:[
      { id:"hm-lunch",title:"Lunch Peak Heatmap (12–2)", type:"heatmap",color:"#f97316" },
      { id:"hm-break",title:"Tea Break Heatmap (4–5)",   type:"heatmap",color:"#ef4444" },
      { id:"hm-peak", title:"Peak Zone",                 type:"stat",value:"Zone A",delta:"High density",up:null,icon:<Map size={16}/>,color:"#f97316" },
    ],
  },
  "Employee Productivity": {
    dashboard:[
      { id:"ep-idle",  title:"Idle Staff Detected", type:"stat",  value:"3",   delta:"+1",   up:false,icon:<AlertTriangle size={16}/>,color:"#f59e0b" },
      { id:"ep-speed", title:"Avg Service Speed",   type:"stat",  value:"4.2m",delta:"-0.3m",up:true, icon:<Zap size={16}/>,          color:"#22c55e" },
      { id:"ep-tasks", title:"Tasks Completed",     type:"stat",  value:"87%", delta:"+3%",  up:true, icon:<CheckCircle2 size={16}/>, color:"#6366f1" },
      { id:"ep-shift", title:"Productivity Score",  type:"gauge", value:84,                           color:"#6366f1" },
      { id:"ep-trend", title:"Productivity Trend",  type:"chart", color:"#22c55e" },
      { id:"ep-tasks2",title:"Task Completion Rate",type:"chart", color:"#6366f1" },
      { id:"ep-events",title:"Productivity Log",    type:"list" },
    ],
    people:[
      { id:"ep-p1",title:"Staff On Floor",      type:"stat",  value:"9",  delta:"Active",up:null,icon:<Users size={16}/>,  color:"#6366f1" },
      { id:"ep-p2",title:"Idle Time Today",     type:"stat",  value:"22m",delta:"-5m",  up:true,icon:<Clock size={16}/>,  color:"#f59e0b" },
      { id:"ep-p3",title:"Avg Speed of Service",type:"chart", color:"#22c55e" },
      { id:"ep-p4",title:"Staff Activity Log",  type:"list" },
    ],
    report:[
      { id:"rp-ep1",title:"Daily Productivity", type:"chart",color:"#6366f1",period:"Today" },
      { id:"rp-ep2",title:"Weekly Productivity",type:"chart",color:"#22c55e",period:"This Week" },
      { id:"rp-ep3",title:"Task Completion Log",type:"table" },
    ],
  },
  "Food Safety & Hygiene": {
    dashboard:[
      { id:"fs-handle", title:"Improper Handling",  type:"stat",  value:"1",  delta:"-2", up:true, icon:<AlertTriangle size={16}/>,color:"#ef4444" },
      { id:"fs-surface",title:"Surface Clean Rate", type:"stat",  value:"96%",delta:"+1%",up:true, icon:<CheckCircle2 size={16}/>, color:"#22c55e" },
      { id:"fs-uncov",  title:"Uncovered Food",     type:"stat",  value:"0",  delta:"Safe",up:null,icon:<ShieldCheck size={16}/>,  color:"#22c55e" },
      { id:"fs-score",  title:"Hygiene Score",      type:"gauge", value:93,                        color:"#22c55e" },
      { id:"fs-trend",  title:"Violations This Week",type:"chart",color:"#ef4444" },
      { id:"fs-surface2",title:"Cleanliness History",type:"chart",color:"#22c55e" },
      { id:"fs-events", title:"Food Safety Log",    type:"list" },
    ],
    alert:[
      { id:"al-fs1",title:"Improper Food Handling",    severity:"high",  camera:"CAM-03",time:"09:45 AM",status:"Open" },
      { id:"al-fs2",title:"Food Left Uncovered",       severity:"medium",camera:"CAM-03",time:"08:30 AM",status:"Resolved" },
      { id:"al-fs3",title:"Surface Not Cleaned (>2hr)",severity:"medium",camera:"CAM-02",time:"Yesterday",status:"Resolved" },
    ],
    camera:[
      { id:"cam-01",title:"CAM-01 · Kitchen",     type:"feed",status:"online", health:"Good",   last:"just now" },
      { id:"cam-02",title:"CAM-02 · Prep Station",type:"feed",status:"online", health:"Good",   last:"1 min ago" },
      { id:"cam-03",title:"CAM-03 · Storage",     type:"feed",status:"offline",health:"Critical",last:"45 min ago" },
    ],
    safety:[
      { id:"sf-fs1",title:"Food Handling Compliance",type:"stat", value:"94%",delta:"+2%",up:true, icon:<ShieldCheck size={16}/>,   color:"#22c55e" },
      { id:"sf-fs2",title:"Hygiene Violations",     type:"stat", value:"3",  delta:"-2", up:true, icon:<AlertTriangle size={16}/>,color:"#ef4444" },
      { id:"sf-fs3",title:"Weekly Hygiene Trend",   type:"chart",color:"#22c55e" },
      { id:"sf-fs4",title:"Zone Violations",        type:"chart",color:"#f59e0b" },
    ],
  },
  "Customer Wait Times": {
    dashboard: [
      { id: "cw-queue",   title: "Avg Queue Length",    type: "stat",  value: "7",    delta: "+2",   up: false, icon: <Users size={16}/>,         color: "#ef4444" },
      { id: "cw-wait",    title: "Avg Wait Time",       type: "stat",  value: "3.8m", delta: "-0.5m",up: true,  icon: <Clock size={16}/>,          color: "#f59e0b" },
      { id: "cw-fulfill", title: "Order Fulfillment",   type: "stat",  value: "5.2m", delta: "-0.8m",up: true,  icon: <CheckCircle2 size={16}/>,   color: "#22c55e" },
      { id: "cw-pos",     title: "POS Bottleneck Score",type: "gauge", value: 62,                               color: "#f59e0b" },
      { id: "cw-trend",   title: "Queue Length (Today)",type: "chart", color: "#ef4444" },
      { id: "cw-wait2",   title: "Wait Time Trend",     type: "chart", color: "#f59e0b" },
      { id: "cw-events",  title: "Queue Events Log",    type: "list" },
    ],
    alert: [
      { id: "al-cw1", title: "Queue Exceeded 10 People",    severity: "high",   camera: "CAM-01", time: "12:15 PM", status: "Open" },
      { id: "al-cw2", title: "Wait Time > 8 min",           severity: "high",   camera: "CAM-01", time: "12:02 PM", status: "Unresolved" },
      { id: "al-cw3", title: "POS Slow Response Detected",  severity: "medium", camera: "CAM-04", time: "11:45 AM", status: "Resolved" },
    ],
    report: [
      { id: "rp-cw1", title: "Daily Wait Time Summary",  type: "chart", color: "#f59e0b", period: "Today" },
      { id: "rp-cw2", title: "Peak Hour Analysis",       type: "chart", color: "#ef4444", period: "This Week" },
      { id: "rp-cw3", title: "Queue Incident Log",       type: "table" },
    ],
    heatmap: [
      { id: "hm-cw1", title: "Lunchtime Queue Heatmap",  type: "heatmap", color: "#ef4444" },
      { id: "hm-cw2", title: "Evening Queue Heatmap",    type: "heatmap", color: "#f97316" },
      { id: "hm-cw3", title: "Busiest Zone",             type: "stat", value: "Counter 1", delta: "Peak zone", up: null, icon: <Map size={16}/>, color: "#ef4444" },
    ],
  },
  "Staff PPE Compliance": {
    dashboard: [
      { id: "ppe-rate",  title: "PPE Compliance Rate",  type: "stat",  value: "89%", delta: "+2%", up: true,  icon: <HardHat size={16}/>,       color: "#22c55e" },
      { id: "ppe-viol",  title: "Violations Today",     type: "stat",  value: "4",   delta: "+1",  up: false, icon: <AlertTriangle size={16}/>,  color: "#ef4444" },
      { id: "ppe-glove", title: "Glove Compliance",     type: "stat",  value: "91%", delta: "+1%", up: true,  icon: <ShieldCheck size={16}/>,    color: "#6366f1" },
      { id: "ppe-gauge", title: "Overall PPE Score",    type: "gauge", value: 89,                             color: "#22c55e" },
      { id: "ppe-trend", title: "PPE Compliance Trend", type: "chart", color: "#22c55e" },
      { id: "ppe-zone",  title: "Zone-wise Violations", type: "chart", color: "#ef4444" },
      { id: "ppe-log",   title: "PPE Violation Log",    type: "list" },
    ],
    safety: [
      { id: "sf-ppe1", title: "Gloves Compliance",  type: "stat",  value: "91%", delta: "+1%", up: true,  icon: <ShieldCheck size={16}/>,   color: "#22c55e" },
      { id: "sf-ppe2", title: "Hairnet Compliance", type: "stat",  value: "88%", delta: "-2%", up: false, icon: <AlertTriangle size={16}/>, color: "#f59e0b" },
      { id: "sf-ppe3", title: "Apron Compliance",   type: "stat",  value: "94%", delta: "+3%", up: true,  icon: <HardHat size={16}/>,       color: "#6366f1" },
      { id: "sf-ppe4", title: "Daily PPE Trend",    type: "chart", color: "#22c55e" },
    ],
    alert: [
      { id: "al-ppe1", title: "Glove Violation Detected",   severity: "high",   camera: "CAM-02", time: "10:15 AM", status: "Open" },
      { id: "al-ppe2", title: "No Hairnet — Staff at Grill", severity: "medium", camera: "CAM-03", time: "09:55 AM", status: "Unresolved" },
      { id: "al-ppe3", title: "Apron Missing — Counter",    severity: "low",    camera: "CAM-01", time: "09:30 AM", status: "Resolved" },
    ],
  },
  "Inventory & Wastage": {
    dashboard: [
      { id: "inv-fill",  title: "Stock Fill Rate",      type: "stat",  value: "74%", delta: "-3%", up: false, icon: <Package size={16}/>,       color: "#f59e0b" },
      { id: "inv-waste", title: "Wastage Events Today", type: "stat",  value: "6",   delta: "+2",  up: false, icon: <AlertTriangle size={16}/>,  color: "#ef4444" },
      { id: "inv-port",  title: "Portion Consistency",  type: "stat",  value: "82%", delta: "+1%", up: true,  icon: <CheckCircle2 size={16}/>,   color: "#22c55e" },
      { id: "inv-gauge", title: "Inventory Health",     type: "gauge", value: 74,                             color: "#f59e0b" },
      { id: "inv-trend", title: "Wastage This Week",    type: "chart", color: "#ef4444" },
      { id: "inv-stock", title: "Stock Level Trend",    type: "chart", color: "#f59e0b" },
      { id: "inv-table", title: "Stock Status Table",   type: "table" },
    ],
    inventory: [
      { id: "in-i1", title: "Low Stock Items",       type: "stat",  value: "5",   delta: "+2",  up: false, icon: <AlertTriangle size={16}/>, color: "#ef4444" },
      { id: "in-i2", title: "Wastage Frequency",     type: "stat",  value: "6x",  delta: "High",up: false, icon: <Package size={16}/>,       color: "#f59e0b" },
      { id: "in-i3", title: "Restock Events",        type: "chart", color: "#22c55e" },
      { id: "in-i4", title: "Inventory Status Table",type: "table" },
    ],
    report: [
      { id: "rp-iv1", title: "Wastage Report",        type: "chart", color: "#ef4444", period: "Today" },
      { id: "rp-iv2", title: "Stock Movement Trend",  type: "chart", color: "#f59e0b", period: "This Week" },
      { id: "rp-iv3", title: "Portion Size Log",      type: "table" },
    ],
  },
  "Shift Scheduling": {
    dashboard: [
      { id: "sh-peak",    title: "Peak Hour Band",       type: "stat",  value: "12–2 PM", delta: "High",  up: null, icon: <Clock size={16}/>,        color: "#f59e0b" },
      { id: "sh-under",   title: "Understaffed Periods", type: "stat",  value: "2",       delta: "+1",    up: false,icon: <AlertTriangle size={16}/>, color: "#ef4444" },
      { id: "sh-busy",    title: "Busiest Zone",         type: "stat",  value: "Counter", delta: "72%",   up: null, icon: <Map size={16}/>,           color: "#6366f1" },
      { id: "sh-gauge",   title: "Staffing Coverage",    type: "gauge", value: 78,                                  color: "#6366f1" },
      { id: "sh-trend",   title: "Hourly Footfall",      type: "chart", color: "#f59e0b" },
      { id: "sh-zone",    title: "Zone Busyness Trend",  type: "chart", color: "#6366f1" },
      { id: "sh-log",     title: "Shift Events Log",     type: "list" },
    ],
    people: [
      { id: "sh-p1", title: "Staff Per Hour",     type: "stat",  value: "8",    delta: "Active",  up: null, icon: <Users size={16}/>,    color: "#6366f1" },
      { id: "sh-p2", title: "Peak Coverage Gap",  type: "stat",  value: "-2",   delta: "Under",   up: false,icon: <AlertTriangle size={16}/>,color: "#ef4444" },
      { id: "sh-p3", title: "Shift Coverage",     type: "chart", color: "#6366f1" },
      { id: "sh-p4", title: "Staff Activity Log", type: "list" },
    ],
    heatmap: [
      { id: "hm-sh1", title: "Morning Busyness Heatmap",   type: "heatmap", color: "#f59e0b" },
      { id: "hm-sh2", title: "Afternoon Busyness Heatmap", type: "heatmap", color: "#ef4444" },
      { id: "hm-sh3", title: "Peak Zone Summary",          type: "stat", value: "Zone B", delta: "72% peak", up: null, icon: <Map size={16}/>, color: "#f97316" },
    ],
    report: [
      { id: "rp-sh1", title: "Peak Hours Analysis",     type: "chart", color: "#f59e0b", period: "Today" },
      { id: "rp-sh2", title: "Weekly Staffing Trends",  type: "chart", color: "#6366f1", period: "This Week" },
      { id: "rp-sh3", title: "Understaffed Period Log", type: "table" },
    ],
  },
  "Cash & POS Reconciliation": {
    dashboard: [
      { id: "pos-till",  title: "Till Activity Today",  type: "stat",  value: "142",  delta: "+12",  up: true,  icon: <DollarSign size={16}/>,    color: "#22c55e" },
      { id: "pos-void",  title: "Frequent Voids",       type: "stat",  value: "5",    delta: "+3",   up: false, icon: <AlertTriangle size={16}/>,  color: "#ef4444" },
      { id: "pos-anom",  title: "Cash Anomalies",       type: "stat",  value: "2",    delta: "High", up: false, icon: <AlertOctagon size={16}/>,   color: "#ef4444" },
      { id: "pos-gauge", title: "Reconciliation Score", type: "gauge", value: 88,                              color: "#22c55e" },
      { id: "pos-trend", title: "Till Activity Trend",  type: "chart", color: "#22c55e" },
      { id: "pos-void2", title: "Void Events Timeline", type: "chart", color: "#ef4444" },
      { id: "pos-table", title: "Anomaly Log",          type: "table" },
    ],
    alert: [
      { id: "al-pos1", title: "Frequent Void Detected (>3)",     severity: "high",   camera: "CAM-04", time: "11:30 AM", status: "Open" },
      { id: "al-pos2", title: "Cash Handling Anomaly",           severity: "high",   camera: "CAM-04", time: "10:55 AM", status: "Unresolved" },
      { id: "al-pos3", title: "Till Opened Without Transaction", severity: "medium", camera: "CAM-04", time: "10:20 AM", status: "Resolved" },
    ],
    report: [
      { id: "rp-pos1", title: "Daily Transaction Summary",  type: "chart", color: "#22c55e", period: "Today" },
      { id: "rp-pos2", title: "Weekly Void Analysis",       type: "chart", color: "#ef4444", period: "This Week" },
      { id: "rp-pos3", title: "Cash Anomaly Log",           type: "table" },
    ],
  },
  "PPE Compliance": {
    dashboard:[
      { id:"mppe-hard", title:"Hard Hat Compliance",type:"stat", value:"92%",delta:"+3%",up:true, icon:<HardHat size={16}/>,    color:"#f97316" },
      { id:"mppe-glass",title:"Safety Glasses",     type:"stat", value:"88%",delta:"+1%",up:true, icon:<Eye size={16}/>,         color:"#3b82f6" },
      { id:"mppe-glove",title:"Gloves Compliance",  type:"stat", value:"90%",delta:"-1%",up:false,icon:<ShieldCheck size={16}/>, color:"#22c55e" },
      { id:"mppe-gauge",title:"Overall PPE Score",  type:"gauge",value:90,                        color:"#22c55e" },
      { id:"mppe-trend",title:"PPE Trend (Weekly)", type:"chart",color:"#22c55e" },
      { id:"mppe-zone", title:"Zone Violations",    type:"chart",color:"#ef4444" },
      { id:"mppe-log",  title:"Violation Log",      type:"list" },
    ],
    safety:[
      { id:"sf-m1",title:"Hard Hat Compliance", type:"stat", value:"92%",delta:"+3%",up:true, icon:<HardHat size={16}/>,       color:"#f97316" },
      { id:"sf-m2",title:"Glasses Violations",  type:"stat", value:"4",  delta:"-2", up:true, icon:<AlertTriangle size={16}/>,color:"#ef4444" },
      { id:"sf-m3",title:"Weekly PPE Trend",    type:"chart",color:"#22c55e" },
      { id:"sf-m4",title:"Zone-wise Breakdown", type:"chart",color:"#f59e0b" },
    ],
    alert:[
      { id:"al-m1",title:"Hard Hat Missing — Zone C",  severity:"high",  camera:"CAM-05",time:"09:20 AM",status:"Open" },
      { id:"al-m2",title:"Safety Glasses Violation",   severity:"high",  camera:"CAM-03",time:"08:45 AM",status:"Unresolved" },
      { id:"al-m3",title:"Glove Compliance Dip (<80%)",severity:"medium",camera:"CAM-02",time:"08:10 AM",status:"Resolved" },
    ],
    camera:[
      { id:"cam-01",title:"CAM-01 · Assembly Floor",type:"feed",status:"online", health:"Good",   last:"just now" },
      { id:"cam-02",title:"CAM-02 · Entry Gate",    type:"feed",status:"online", health:"Good",   last:"1 min ago" },
      { id:"cam-03",title:"CAM-03 · Hazard Zone",   type:"feed",status:"online", health:"Average",last:"5 min ago" },
    ],
  },
  "Line Productivity Monitoring": {
    dashboard: [
      { id: "lp-idle",   title: "Machine Idle Time",    type: "stat",  value: "12m",  delta: "-5m",  up: true,  icon: <Cpu size={16}/>,           color: "#f59e0b" },
      { id: "lp-down",   title: "Operator Downtime",    type: "stat",  value: "18m",  delta: "-8m",  up: true,  icon: <Users size={16}/>,          color: "#6366f1" },
      { id: "lp-cycle",  title: "Avg Cycle Time",       type: "stat",  value: "2.4m", delta: "-0.2m",up: true,  icon: <Clock size={16}/>,          color: "#22c55e" },
      { id: "lp-gauge",  title: "Line Efficiency",      type: "gauge", value: 88,                               color: "#22c55e" },
      { id: "lp-trend",  title: "Hourly Output",        type: "chart", color: "#22c55e" },
      { id: "lp-cycle2", title: "Cycle Time Trend",     type: "chart", color: "#6366f1" },
      { id: "lp-table",  title: "Downtime Log",         type: "table" },
    ],
    production: [
      { id: "pd-lp1", title: "Line Speed",        type: "stat",  value: "94 u/h", delta: "+6",  up: true,  icon: <Zap size={16}/>,     color: "#22c55e" },
      { id: "pd-lp2", title: "Idle Machines",     type: "stat",  value: "1",      delta: "-2",  up: true,  icon: <Cpu size={16}/>,     color: "#f59e0b" },
      { id: "pd-lp3", title: "Production Trend",  type: "chart", color: "#22c55e" },
      { id: "pd-lp4", title: "Efficiency Trend",  type: "chart", color: "#6366f1" },
    ],
    report: [
      { id: "rp-lp1", title: "Daily Line Summary",   type: "chart", color: "#22c55e", period: "Today" },
      { id: "rp-lp2", title: "Weekly Throughput",    type: "chart", color: "#6366f1", period: "This Week" },
      { id: "rp-lp3", title: "Downtime Report",      type: "table" },
    ],
  },
  "Quality Defect Detection": {
    dashboard: [
      { id: "qd-prod",   title: "Product Defects",    type: "stat",  value: "11",   delta: "-4",   up: true,  icon: <XCircle size={16}/>,       color: "#ef4444" },
      { id: "qd-pack",   title: "Packaging Defects",  type: "stat",  value: "3",    delta: "-1",   up: true,  icon: <Package size={16}/>,        color: "#f59e0b" },
      { id: "qd-rate",   title: "Defect Rate",         type: "stat",  value: "1.2%", delta: "-0.3%",up: true,  icon: <TrendingUp size={16}/>,     color: "#6366f1" },
      { id: "qd-gauge",  title: "Quality Score",       type: "gauge", value: 94,                              color: "#22c55e" },
      { id: "qd-trend",  title: "Defects This Week",   type: "chart", color: "#ef4444" },
      { id: "qd-type",   title: "Defect Breakdown",    type: "chart", color: "#f59e0b" },
      { id: "qd-table",  title: "Defect Report Log",   type: "table" },
    ],
    production: [
      { id: "pd-qd1", title: "Defects Detected",    type: "stat",  value: "11",   delta: "-4",  up: true, icon: <XCircle size={16}/>,  color: "#ef4444" },
      { id: "pd-qd2", title: "Packaging Defects",   type: "stat",  value: "3",    delta: "-1",  up: true, icon: <Package size={16}/>,  color: "#f59e0b" },
      { id: "pd-qd3", title: "Defect Rate Trend",   type: "chart", color: "#ef4444" },
      { id: "pd-qd4", title: "Defect Type Breakdown",type: "chart",color: "#f59e0b" },
    ],
    alert: [
      { id: "al-qd1", title: "Product Defect Cluster (>5)",   severity: "high",   camera: "CAM-05", time: "10:30 AM", status: "Open" },
      { id: "al-qd2", title: "Packaging Defect Detected",     severity: "medium", camera: "CAM-06", time: "09:50 AM", status: "Unresolved" },
      { id: "al-qd3", title: "Defect Rate Spike",             severity: "high",   camera: "CAM-05", time: "09:15 AM", status: "Resolved" },
    ],
  },
  "Unauthorised Zone Access": {
    dashboard: [
      { id: "uz-entry",  title: "Restricted Zone Entries", type: "stat",  value: "3",   delta: "+1",  up: false, icon: <ShieldAlert size={16}/>,   color: "#ef4444" },
      { id: "uz-haz",    title: "Hazard Area Breaches",    type: "stat",  value: "1",   delta: "New", up: false, icon: <AlertOctagon size={16}/>,  color: "#ef4444" },
      { id: "uz-clear",  title: "Cleared Zones",           type: "stat",  value: "6/8", delta: "Safe",up: null,  icon: <CheckCircle2 size={16}/>,  color: "#22c55e" },
      { id: "uz-gauge",  title: "Zone Security Score",     type: "gauge", value: 82,                             color: "#f59e0b" },
      { id: "uz-trend",  title: "Access Violations Today", type: "chart", color: "#ef4444" },
      { id: "uz-zone",   title: "Zone-wise Breaches",      type: "chart", color: "#f97316" },
      { id: "uz-log",    title: "Zone Access Log",         type: "list" },
    ],
    alert: [
      { id: "al-uz1", title: "Restricted Zone Entry Detected",   severity: "high",   camera: "CAM-02", time: "11:05 AM", status: "Open" },
      { id: "al-uz2", title: "Hazardous Area Breach",            severity: "high",   camera: "CAM-04", time: "10:50 AM", status: "Unresolved" },
      { id: "al-uz3", title: "Unauthorized Visitor in Zone D",   severity: "medium", camera: "CAM-03", time: "10:20 AM", status: "Resolved" },
    ],
    camera: [
      { id: "cam-01", title: "CAM-01 · Restricted Zone A",   type: "feed", status: "online",  health: "Good",     last: "just now" },
      { id: "cam-02", title: "CAM-02 · Hazard Area",         type: "feed", status: "online",  health: "Good",     last: "1 min ago" },
      { id: "cam-03", title: "CAM-03 · Entry Gate",          type: "feed", status: "online",  health: "Average",  last: "3 min ago" },
      { id: "cam-04", title: "CAM-04 · Storage Restricted",  type: "feed", status: "offline", health: "Critical", last: "1 hr ago" },
    ],
  },
  "Equipment Anomaly Detection": {
    dashboard: [
      { id: "eq-smoke",  title: "Smoke Events",         type: "stat",  value: "0",   delta: "Clear",up: null, icon: <Thermometer size={16}/>,   color: "#22c55e" },
      { id: "eq-spark",  title: "Spark Detections",     type: "stat",  value: "1",   delta: "Alert",up: false,icon: <Zap size={16}/>,            color: "#ef4444" },
      { id: "eq-abn",    title: "Abnormal Activity",    type: "stat",  value: "2",   delta: "+1",   up: false,icon: <AlertTriangle size={16}/>,  color: "#f59e0b" },
      { id: "eq-gauge",  title: "Equipment Health",     type: "gauge", value: 87,                            color: "#22c55e" },
      { id: "eq-trend",  title: "Anomaly Events Trend", type: "chart", color: "#ef4444" },
      { id: "eq-type",   title: "Anomaly Breakdown",    type: "chart", color: "#f59e0b" },
      { id: "eq-log",    title: "Equipment Event Log",  type: "list" },
    ],
    alert: [
      { id: "al-eq1", title: "Spark Detected — Machine 3",        severity: "high",   camera: "CAM-03", time: "10:45 AM", status: "Open" },
      { id: "al-eq2", title: "Abnormal Machine Vibration",        severity: "medium", camera: "CAM-02", time: "10:10 AM", status: "Unresolved" },
      { id: "al-eq3", title: "Smoke False Positive — Cleared",    severity: "low",    camera: "CAM-05", time: "09:40 AM", status: "Resolved" },
    ],
    camera: [
      { id: "cam-01", title: "CAM-01 · Machine Bay 1",    type: "feed", status: "online",  health: "Good",    last: "just now" },
      { id: "cam-02", title: "CAM-02 · Machine Bay 2",    type: "feed", status: "online",  health: "Good",    last: "2 min ago" },
      { id: "cam-03", title: "CAM-03 · High-Risk Zone",   type: "feed", status: "online",  health: "Average", last: "5 min ago" },
    ],
    production: [
      { id: "pd-eq1", title: "Machine Health Score",   type: "stat",  value: "87%",  delta: "-3%", up: false, icon: <Cpu size={16}/>,          color: "#f59e0b" },
      { id: "pd-eq2", title: "Anomalies This Shift",   type: "stat",  value: "3",    delta: "+1",  up: false, icon: <AlertTriangle size={16}/>,color: "#ef4444" },
      { id: "pd-eq3", title: "Anomaly Trend",          type: "chart", color: "#ef4444" },
      { id: "pd-eq4", title: "Machine Activity Log",   type: "chart", color: "#6366f1" },
    ],
  },
  "Ergonomics & Fatigue": {
    dashboard: [
      { id: "ef-fat",   title: "Fatigue Detections",    type: "stat",  value: "4",   delta: "+2",  up: false, icon: <AlertTriangle size={16}/>, color: "#f59e0b" },
      { id: "ef-post",  title: "Unsafe Postures",       type: "stat",  value: "7",   delta: "-3",  up: true,  icon: <Users size={16}/>,          color: "#6366f1" },
      { id: "ef-risk",  title: "High-Risk Workers",     type: "stat",  value: "2",   delta: "Flag",up: false, icon: <ShieldAlert size={16}/>,    color: "#ef4444" },
      { id: "ef-gauge", title: "Ergonomics Score",      type: "gauge", value: 76,                             color: "#f59e0b" },
      { id: "ef-trend", title: "Fatigue Events Trend",  type: "chart", color: "#f59e0b" },
      { id: "ef-post2", title: "Posture Risk Trend",    type: "chart", color: "#6366f1" },
      { id: "ef-log",   title: "Ergonomics Event Log",  type: "list" },
    ],
    people: [
      { id: "pp-ef1", title: "Workers Flagged",      type: "stat",  value: "2",   delta: "High Risk",  up: false, icon: <UserCheck size={16}/>,    color: "#ef4444" },
      { id: "pp-ef2", title: "Fatigue Score",        type: "stat",  value: "76%", delta: "-4%",        up: false, icon: <TrendingUp size={16}/>,   color: "#f59e0b" },
      { id: "pp-ef3", title: "Fatigue Timeline",     type: "chart", color: "#f59e0b" },
      { id: "pp-ef4", title: "At-Risk Worker Log",   type: "list" },
    ],
    alert: [
      { id: "al-ef1", title: "Worker Fatigue Detected",          severity: "high",   camera: "CAM-04", time: "02:30 PM", status: "Open" },
      { id: "al-ef2", title: "Unsafe Posture — Line B Worker",   severity: "medium", camera: "CAM-02", time: "01:55 PM", status: "Unresolved" },
      { id: "al-ef3", title: "Ergonomic Alert Resolved",         severity: "low",    camera: "CAM-01", time: "01:20 PM", status: "Resolved" },
    ],
  },
  "Visitor & Contractor Management": {
    dashboard: [
      { id: "vc-vis",   title: "Visitors Today",        type: "stat",  value: "12",  delta: "+3",  up: null, icon: <Users size={16}/>,         color: "#6366f1" },
      { id: "vc-unauth",title: "Unauthorized Detected", type: "stat",  value: "1",   delta: "Alert",up: false,icon: <ShieldAlert size={16}/>,   color: "#ef4444" },
      { id: "vc-fall",  title: "Incidents (Fall/Fight)",type: "stat",  value: "0",   delta: "Clear",up: null, icon: <ShieldCheck size={16}/>,   color: "#22c55e" },
      { id: "vc-gauge", title: "Visitor Safety Score",  type: "gauge", value: 92,                            color: "#22c55e" },
      { id: "vc-trend", title: "Visitor Traffic Today", type: "chart", color: "#6366f1" },
      { id: "vc-zone",  title: "Zone Presence",         type: "chart", color: "#f59e0b" },
      { id: "vc-log",   title: "Visitor Event Log",     type: "list" },
    ],
    camera: [
      { id: "cam-01", title: "CAM-01 · Visitor Entry",     type: "feed", status: "online",  health: "Good",    last: "just now" },
      { id: "cam-02", title: "CAM-02 · Reception Area",    type: "feed", status: "online",  health: "Good",    last: "2 min ago" },
      { id: "cam-03", title: "CAM-03 · Contractor Zone",   type: "feed", status: "online",  health: "Average", last: "5 min ago" },
      { id: "cam-04", title: "CAM-04 · Exit Gate",         type: "feed", status: "offline", health: "Critical",last: "1 hr ago" },
    ],
    alert: [
      { id: "al-vc1", title: "Unauthorized Contractor Detected", severity: "high",   camera: "CAM-03", time: "11:40 AM", status: "Open" },
      { id: "al-vc2", title: "Visitor in Restricted Zone",       severity: "medium", camera: "CAM-02", time: "11:15 AM", status: "Unresolved" },
      { id: "al-vc3", title: "Visitor Exit Not Logged",          severity: "low",    camera: "CAM-01", time: "10:50 AM", status: "Resolved" },
    ],
    people: [
      { id: "pp-vc1", title: "Visitors On-site",   type: "stat",  value: "12", delta: "+3",   up: null, icon: <Users size={16}/>,    color: "#6366f1" },
      { id: "pp-vc2", title: "Contractors Active", type: "stat",  value: "5",  delta: "Noted",up: null, icon: <Wrench size={16}/>,   color: "#f59e0b" },
      { id: "pp-vc3", title: "Visitor Timeline",   type: "chart", color: "#6366f1" },
      { id: "pp-vc4", title: "Visitor Movement Log",type: "list" },
    ],
  },
  "Footfall & Heatmapping": {
    dashboard: [
      { id: "ft-entry",  title: "Customer Entries",     type: "stat",  value: "284",  delta: "+22",  up: true,  icon: <ArrowUpRight size={16}/>,  color: "#8b5cf6" },
      { id: "ft-exit",   title: "Customer Exits",       type: "stat",  value: "261",  delta: "+18",  up: true,  icon: <ArrowDownRight size={16}/>,color: "#6366f1" },
      { id: "ft-dwell",  title: "Avg Dwell Time",       type: "stat",  value: "12m",  delta: "+2m",  up: true,  icon: <Clock size={16}/>,          color: "#f59e0b" },
      { id: "ft-gauge",  title: "Store Utilisation",    type: "gauge", value: 68,                               color: "#8b5cf6" },
      { id: "ft-trend",  title: "Hourly Footfall",      type: "chart", color: "#8b5cf6" },
      { id: "ft-dwell2", title: "Dwell Time Trend",     type: "chart", color: "#f59e0b" },
      { id: "ft-log",    title: "Traffic Events Log",   type: "list" },
    ],
    heatmap: [
      { id: "hm-ft1", title: "Morning Footfall Heatmap",    type: "heatmap", color: "#8b5cf6" },
      { id: "hm-ft2", title: "Afternoon Footfall Heatmap",  type: "heatmap", color: "#6366f1" },
      { id: "hm-ft3", title: "Peak Traffic Zone",           type: "stat", value: "Aisle B", delta: "High traffic", up: null, icon: <Map size={16}/>, color: "#8b5cf6" },
    ],
    report: [
      { id: "rp-ft1", title: "Daily Footfall Summary",   type: "chart", color: "#8b5cf6", period: "Today" },
      { id: "rp-ft2", title: "Weekly Footfall Trend",    type: "chart", color: "#6366f1", period: "This Week" },
      { id: "rp-ft3", title: "High Traffic Zone Report", type: "table" },
    ],
  },
  "Queue Management": {
    dashboard:[
      { id:"qm-len",  title:"Avg Queue Length", type:"stat", value:"9",   delta:"+3",   up:false,icon:<Users size={16}/>,        color:"#ef4444" },
      { id:"qm-svc",  title:"Avg Service Time", type:"stat", value:"3.1m",delta:"-0.4m",up:true, icon:<Clock size={16}/>,         color:"#22c55e" },
      { id:"qm-chk",  title:"Checkouts Active", type:"stat", value:"4/6", delta:"-1",   up:false,icon:<CheckCircle2 size={16}/>,  color:"#f59e0b" },
      { id:"qm-gauge",title:"Queue Health Score",type:"gauge",value:71,                           color:"#f59e0b" },
      { id:"qm-trend",title:"Queue Length (Today)",type:"chart",color:"#ef4444" },
      { id:"qm-svc2", title:"Service Time Trend",  type:"chart",color:"#22c55e" },
      { id:"qm-log",  title:"Queue Events Log",    type:"list" },
    ],
    alert:[
      { id:"al-qm1",title:"Queue Exceeded 12 People",severity:"high",  camera:"CAM-01",time:"12:30 PM",status:"Open" },
      { id:"al-qm2",title:"Checkout Lane Closed",    severity:"medium",camera:"CAM-01",time:"12:00 PM",status:"Unresolved" },
      { id:"al-qm3",title:"Service Time Normalized", severity:"low",   camera:"CAM-01",time:"11:45 AM",status:"Resolved" },
    ],
    camera:[
      { id:"cam-01",title:"CAM-01 · Checkout Area",type:"feed",status:"online", health:"Good",   last:"just now" },
      { id:"cam-02",title:"CAM-02 · Main Entrance",type:"feed",status:"online", health:"Good",   last:"1 min ago" },
      { id:"cam-03",title:"CAM-03 · Express Lane", type:"feed",status:"offline",health:"Critical",last:"2 hr ago" },
    ],
    report:[
      { id:"rp-qm1",title:"Daily Queue Summary",type:"chart",color:"#ef4444",period:"Today" },
      { id:"rp-qm2",title:"Service Time Trend", type:"chart",color:"#22c55e",period:"This Week" },
      { id:"rp-qm3",title:"Queue Incident Log", type:"table" },
    ],
  },
  "Theft & Loss Prevention": {
    dashboard: [
      { id: "tl-susp",   title: "Suspicious Events",    type: "stat",  value: "3",   delta: "+1",  up: false, icon: <AlertOctagon size={16}/>,  color: "#ef4444" },
      { id: "tl-sweep",  title: "Shelf Sweep Alerts",   type: "stat",  value: "1",   delta: "New", up: false, icon: <AlertTriangle size={16}/>,  color: "#ef4444" },
      { id: "tl-inc",    title: "Fight/Fall Incidents",  type: "stat",  value: "0",   delta: "Clear",up: null, icon: <ShieldCheck size={16}/>,   color: "#22c55e" },
      { id: "tl-gauge",  title: "Security Score",       type: "gauge", value: 84,                             color: "#22c55e" },
      { id: "tl-trend",  title: "Suspicious Events",    type: "chart", color: "#ef4444" },
      { id: "tl-zone",   title: "Hotspot Zones",        type: "chart", color: "#f97316" },
      { id: "tl-log",    title: "Security Event Log",   type: "list" },
    ],
    camera: [
      { id: "cam-01", title: "CAM-01 · Electronics Aisle",  type: "feed", status: "online",  health: "Good",    last: "just now" },
      { id: "cam-02", title: "CAM-02 · Exit Gate",          type: "feed", status: "online",  health: "Good",    last: "1 min ago" },
      { id: "cam-03", title: "CAM-03 · High-Value Zone",    type: "feed", status: "online",  health: "Average", last: "5 min ago" },
      { id: "cam-04", title: "CAM-04 · Parking Entrance",   type: "feed", status: "offline", health: "Critical",last: "3 hr ago" },
    ],
    alert: [
      { id: "al-tl1", title: "Suspicious Behavior Detected",   severity: "high",   camera: "CAM-01", time: "02:15 PM", status: "Open" },
      { id: "al-tl2", title: "Shelf Sweep Alert",              severity: "high",   camera: "CAM-03", time: "01:50 PM", status: "Unresolved" },
      { id: "al-tl3", title: "Fight Alert — Resolved",         severity: "low",    camera: "CAM-02", time: "01:20 PM", status: "Resolved" },
    ],
  },
  "Planogram Compliance": {
    dashboard: [
      { id: "pg-oos",   title: "Out-of-Stock Shelves",  type: "stat",  value: "4",   delta: "+2",  up: false, icon: <Package size={16}/>,       color: "#ef4444" },
      { id: "pg-place", title: "Placement Accuracy",    type: "stat",  value: "91%", delta: "+1%", up: true,  icon: <CheckCircle2 size={16}/>,  color: "#22c55e" },
      { id: "pg-fill",  title: "Shelf Fill Rate",       type: "stat",  value: "78%", delta: "-3%", up: false, icon: <Layers size={16}/>,        color: "#f59e0b" },
      { id: "pg-gauge", title: "Planogram Score",       type: "gauge", value: 84,                             color: "#22c55e" },
      { id: "pg-trend", title: "OOS Events This Week",  type: "chart", color: "#ef4444" },
      { id: "pg-fill2", title: "Shelf Fill Trend",      type: "chart", color: "#f59e0b" },
      { id: "pg-table", title: "Shelf Status Table",    type: "table" },
    ],
    inventory: [
      { id: "in-pg1", title: "Out-of-Stock Items",    type: "stat",  value: "4",   delta: "+2",  up: false, icon: <AlertTriangle size={16}/>, color: "#ef4444" },
      { id: "in-pg2", title: "Misplaced Products",    type: "stat",  value: "6",   delta: "+1",  up: false, icon: <Package size={16}/>,       color: "#f59e0b" },
      { id: "in-pg3", title: "Restock Events",        type: "chart", color: "#22c55e" },
      { id: "in-pg4", title: "Shelf Status Table",    type: "table" },
    ],
    camera: [
      { id: "cam-01", title: "CAM-01 · Aisle A",     type: "feed", status: "online",  health: "Good",    last: "just now" },
      { id: "cam-02", title: "CAM-02 · Aisle B",     type: "feed", status: "online",  health: "Good",    last: "1 min ago" },
      { id: "cam-03", title: "CAM-03 · Aisle C",     type: "feed", status: "online",  health: "Average", last: "5 min ago" },
      { id: "cam-04", title: "CAM-04 · End Caps",    type: "feed", status: "offline", health: "Critical",last: "2 hr ago" },
    ],
  },
  "Staff Deployment": {
    dashboard: [
      { id: "sd-floor",  title: "Staff On Floor",      type: "stat",  value: "11",  delta: "+2",  up: true,  icon: <Users size={16}/>,          color: "#8b5cf6" },
      { id: "sd-idle",   title: "Idle Staff",          type: "stat",  value: "2",   delta: "-1",  up: true,  icon: <AlertTriangle size={16}/>,  color: "#f59e0b" },
      { id: "sd-density",title: "Customer Density",    type: "stat",  value: "High",delta: "Peak",up: null,  icon: <Flame size={16}/>,           color: "#ef4444" },
      { id: "sd-gauge",  title: "Deployment Score",    type: "gauge", value: 82,                             color: "#8b5cf6" },
      { id: "sd-trend",  title: "Staff vs Traffic",    type: "chart", color: "#8b5cf6" },
      { id: "sd-zone",   title: "Zone Staffing",       type: "chart", color: "#6366f1" },
      { id: "sd-log",    title: "Deployment Log",      type: "list" },
    ],
    people: [
      { id: "pp-sd1", title: "Floor Coverage",      type: "stat",  value: "82%", delta: "+3%",  up: true,  icon: <Users size={16}/>,     color: "#8b5cf6" },
      { id: "pp-sd2", title: "Idle Staff Count",    type: "stat",  value: "2",   delta: "-1",   up: true,  icon: <AlertTriangle size={16}/>,color: "#f59e0b" },
      { id: "pp-sd3", title: "Staff Timeline",      type: "chart", color: "#8b5cf6" },
      { id: "pp-sd4", title: "Staff Movement Log",  type: "list" },
    ],
    heatmap: [
      { id: "hm-sd1", title: "Morning Customer Heatmap",   type: "heatmap", color: "#8b5cf6" },
      { id: "hm-sd2", title: "Afternoon Customer Heatmap", type: "heatmap", color: "#ef4444" },
      { id: "hm-sd3", title: "Peak Zone",                  type: "stat", value: "Zone C", delta: "80% density", up: null, icon: <Map size={16}/>, color: "#8b5cf6" },
    ],
  },
  "Parking Occupancy Monitoring": {
    dashboard: [
      { id:"po-avail",  title:"Available Slots",       type:"stat",  value:"34/120", delta:"-8",   up:false, icon:<Package size={16}/>,        color:"#22c55e" },
      { id:"po-occ",    title:"Occupied Slots",         type:"stat",  value:"86",     delta:"+8",   up:false, icon:<Users size={16}/>,           color:"#f97316" },
      { id:"po-util",   title:"Parking Utilisation",   type:"stat",  value:"71%",    delta:"+5%",  up:false, icon:<TrendingUp size={16}/>,      color:"#f59e0b" },
      { id:"po-gauge",  title:"Occupancy Score",        type:"gauge", value:71,                               color:"#f97316" },
      { id:"po-trend",  title:"Occupancy Trend (Today)",type:"chart", color:"#f97316" },
      { id:"po-zone",   title:"Zone-wise Occupancy",   type:"chart", color:"#f59e0b" },
      { id:"po-log",    title:"Occupancy Event Log",   type:"list" },
    ],
    alert: [
      { id:"al-po1", title:"Parking Full — Level B2",          severity:"high",   camera:"CAM-04", time:"11:30 AM", status:"Open" },
      { id:"al-po2", title:"Occupancy Exceeded 80%",           severity:"medium", camera:"CAM-01", time:"10:55 AM", status:"Unresolved" },
      { id:"al-po3", title:"Slots Available — Level A1",       severity:"low",    camera:"CAM-02", time:"10:10 AM", status:"Resolved" },
    ],
    camera: [
      { id:"cam-01", title:"CAM-01 · Level A Entry",     type:"feed", status:"online",  health:"Good",    last:"just now" },
      { id:"cam-02", title:"CAM-02 · Level A Overview",  type:"feed", status:"online",  health:"Good",    last:"1 min ago" },
      { id:"cam-03", title:"CAM-03 · Level B Entry",     type:"feed", status:"online",  health:"Average", last:"3 min ago" },
      { id:"cam-04", title:"CAM-04 · Level B Overview",  type:"feed", status:"offline", health:"Critical",last:"45 min ago" },
    ],
    report: [
      { id:"rp-po1", title:"Daily Occupancy Summary",  type:"chart", color:"#f97316", period:"Today" },
      { id:"rp-po2", title:"Weekly Utilisation Trend", type:"chart", color:"#f59e0b", period:"This Week" },
      { id:"rp-po3", title:"Occupancy Status Report",  type:"table" },
    ],
    heatmap: [
      { id:"hm-po1", title:"Morning Occupancy Heatmap",    type:"heatmap", color:"#f97316" },
      { id:"hm-po2", title:"Peak Hour Occupancy Heatmap",  type:"heatmap", color:"#ef4444" },
      { id:"hm-po3", title:"Busiest Zone",                 type:"stat", value:"Level B2", delta:"High density", up:null, icon:<Map size={16}/>, color:"#f97316" },
    ],
  },
  "Vehicle Counting": {
    dashboard: [
      { id:"vc2-entry", title:"Vehicles Entered Today",  type:"stat",  value:"312",   delta:"+28",  up:true,  icon:<ArrowUpRight size={16}/>,   color:"#6366f1" },
      { id:"vc2-exit",  title:"Vehicles Exited Today",   type:"stat",  value:"289",   delta:"+22",  up:true,  icon:<ArrowDownRight size={16}/>, color:"#22c55e" },
      { id:"vc2-net",   title:"Current Vehicles Inside", type:"stat",  value:"23",    delta:"+6",   up:false, icon:<Activity size={16}/>,       color:"#f59e0b" },
      { id:"vc2-gauge", title:"Traffic Load Score",      type:"gauge", value:65,                              color:"#6366f1" },
      { id:"vc2-trend", title:"Entry/Exit Trend (Today)",type:"chart", color:"#6366f1" },
      { id:"vc2-peak",  title:"Peak Hour Analysis",      type:"chart", color:"#f59e0b" },
      { id:"vc2-log",   title:"Vehicle Count Log",       type:"list" },
    ],
    alert: [
      { id:"al-vc2-1", title:"Traffic Surge — Entry Gate 1",   severity:"high",   camera:"CAM-01", time:"12:15 PM", status:"Open" },
      { id:"al-vc2-2", title:"Vehicle Count Mismatch Detected",severity:"medium", camera:"CAM-02", time:"11:40 AM", status:"Unresolved" },
      { id:"al-vc2-3", title:"Peak Hour Ended — Flow Normal",  severity:"low",    camera:"CAM-01", time:"11:00 AM", status:"Resolved" },
    ],
    camera: [
      { id:"cam-01", title:"CAM-01 · Main Entry Gate",   type:"feed", status:"online",  health:"Good",    last:"just now" },
      { id:"cam-02", title:"CAM-02 · Main Exit Gate",    type:"feed", status:"online",  health:"Good",    last:"1 min ago" },
      { id:"cam-03", title:"CAM-03 · Secondary Entry",   type:"feed", status:"online",  health:"Average", last:"4 min ago" },
      { id:"cam-04", title:"CAM-04 · Secondary Exit",    type:"feed", status:"offline", health:"Critical",last:"1 hr ago" },
    ],
    report: [
      { id:"rp-vc2-1", title:"Daily Vehicle Count",      type:"chart", color:"#6366f1", period:"Today" },
      { id:"rp-vc2-2", title:"Weekly Traffic Trend",     type:"chart", color:"#f59e0b", period:"This Week" },
      { id:"rp-vc2-3", title:"Peak Hour Report",         type:"table" },
    ],
  },
  "Illegal Parking Detection": {
    dashboard: [
      { id:"ip-viol",   title:"Violations Today",         type:"stat",  value:"7",   delta:"+3",   up:false, icon:<AlertOctagon size={16}/>,   color:"#ef4444" },
      { id:"ip-nopk",   title:"No-Parking Breaches",      type:"stat",  value:"5",   delta:"+2",   up:false, icon:<ShieldAlert size={16}/>,    color:"#ef4444" },
      { id:"ip-dbl",    title:"Double Parking Alerts",    type:"stat",  value:"2",   delta:"+1",   up:false, icon:<AlertTriangle size={16}/>,  color:"#f97316" },
      { id:"ip-gauge",  title:"Compliance Score",         type:"gauge", value:68,                             color:"#f59e0b" },
      { id:"ip-trend",  title:"Violation Trend (Today)",  type:"chart", color:"#ef4444" },
      { id:"ip-zone",   title:"Hotspot Zones",            type:"chart", color:"#f97316" },
      { id:"ip-log",    title:"Violation Event Log",      type:"list" },
    ],
    alert: [
      { id:"al-ip1", title:"No-Parking Zone Violation",      severity:"high",   camera:"CAM-03", time:"01:50 PM", status:"Open" },
      { id:"al-ip2", title:"Double Parking — Aisle C",       severity:"high",   camera:"CAM-02", time:"01:20 PM", status:"Unresolved" },
      { id:"al-ip3", title:"Violation Cleared — Gate Area",  severity:"low",    camera:"CAM-01", time:"12:45 PM", status:"Resolved" },
    ],
    camera: [
      { id:"cam-01", title:"CAM-01 · Restricted Zone A",  type:"feed", status:"online",  health:"Good",    last:"just now" },
      { id:"cam-02", title:"CAM-02 · Aisle C Overview",   type:"feed", status:"online",  health:"Good",    last:"2 min ago" },
      { id:"cam-03", title:"CAM-03 · No-Parking Zone",    type:"feed", status:"online",  health:"Average", last:"5 min ago" },
      { id:"cam-04", title:"CAM-04 · Exit Ramp",          type:"feed", status:"offline", health:"Critical",last:"2 hr ago" },
    ],
    report: [
      { id:"rp-ip1", title:"Daily Violation Summary",     type:"chart", color:"#ef4444", period:"Today" },
      { id:"rp-ip2", title:"Weekly Violation Trend",      type:"chart", color:"#f97316", period:"This Week" },
      { id:"rp-ip3", title:"Violation Incident Log",      type:"table" },
    ],
  },
  "Parking Duration Tracking": {
    dashboard: [
      { id:"pd2-avg",   title:"Avg Parking Duration",     type:"stat",  value:"42m",  delta:"+7m",  up:false, icon:<Clock size={16}/>,          color:"#f59e0b" },
      { id:"pd2-over",  title:"Overstay Alerts",          type:"stat",  value:"4",    delta:"+2",   up:false, icon:<AlertTriangle size={16}/>,  color:"#ef4444" },
      { id:"pd2-long",  title:"Longest Stay",             type:"stat",  value:"3h 12m",delta:"Flag",up:false, icon:<ShieldAlert size={16}/>,    color:"#ef4444" },
      { id:"pd2-gauge", title:"Duration Health Score",    type:"gauge", value:74,                             color:"#f59e0b" },
      { id:"pd2-trend", title:"Avg Stay Duration Trend",  type:"chart", color:"#f59e0b" },
      { id:"pd2-over2", title:"Overstay Events Timeline", type:"chart", color:"#ef4444" },
      { id:"pd2-log",   title:"Duration Event Log",       type:"list" },
    ],
    alert: [
      { id:"al-pd2-1", title:"Overstay Detected — Slot B14",   severity:"high",   camera:"CAM-02", time:"02:30 PM", status:"Open" },
      { id:"al-pd2-2", title:"Vehicle >3hr — Level A Slot 7",  severity:"medium", camera:"CAM-01", time:"01:45 PM", status:"Unresolved" },
      { id:"al-pd2-3", title:"Overstay Resolved — Slot C3",    severity:"low",    camera:"CAM-03", time:"01:00 PM", status:"Resolved" },
    ],
    camera: [
      { id:"cam-01", title:"CAM-01 · Level A Slots",      type:"feed", status:"online",  health:"Good",    last:"just now" },
      { id:"cam-02", title:"CAM-02 · Level B Slots",      type:"feed", status:"online",  health:"Good",    last:"1 min ago" },
      { id:"cam-03", title:"CAM-03 · Level C Slots",      type:"feed", status:"online",  health:"Average", last:"6 min ago" },
      { id:"cam-04", title:"CAM-04 · Rooftop Level",      type:"feed", status:"offline", health:"Critical",last:"3 hr ago" },
    ],
    report: [
      { id:"rp-pd2-1", title:"Daily Duration Summary",    type:"chart", color:"#f59e0b", period:"Today" },
      { id:"rp-pd2-2", title:"Weekly Overstay Trend",     type:"chart", color:"#ef4444", period:"This Week" },
      { id:"rp-pd2-3", title:"Overstay Incident Log",     type:"table" },
    ],
  },
};

const FALLBACK_WIDGETS = {
  dashboard:[
    { id:"fb-d1",title:"Total Alerts Today", type:"stat", value:"24",   delta:"+3",  up:false,icon:<Bell size={16}/>,          color:"#ef4444" },
    { id:"fb-d2",title:"Cameras Online",     type:"stat", value:"18/21",delta:"+2",  up:true, icon:<Video size={16}/>,          color:"#22c55e" },
    { id:"fb-d3",title:"Current Occupancy",  type:"stat", value:"68%",  delta:"+5%", up:true, icon:<Users size={16}/>,          color:"#3b82f6" },
    { id:"fb-d4",title:"Open Incidents",     type:"stat", value:"3",    delta:"-1",  up:true, icon:<AlertTriangle size={16}/>,  color:"#f59e0b" },
    { id:"fb-d5",title:"Hourly Traffic",     type:"chart",color:"#6366f1" },
    { id:"fb-d6",title:"Zone Activity",      type:"chart",color:"#0ea5e9" },
    { id:"fb-d7",title:"Recent Events",      type:"list" },
    { id:"fb-d8",title:"System Uptime",      type:"gauge",value:97,color:"#10b981" },
  ],
  camera:[
    { id:"cam-01",title:"CAM-01 · Entrance",  type:"feed",status:"online", health:"Good",   last:"just now" },
    { id:"cam-02",title:"CAM-02 · Main Area", type:"feed",status:"online", health:"Average",last:"2 min ago" },
    { id:"cam-03",title:"CAM-03 · Storage",   type:"feed",status:"offline",health:"Critical",last:"1 hr ago" },
    { id:"cam-04",title:"CAM-04 · Exit",      type:"feed",status:"online", health:"Good",   last:"just now" },
  ],
  alert:[
    { id:"al-01",title:"Crowd Limit Exceeded",severity:"high",  camera:"CAM-01",time:"10:42 AM",status:"Open" },
    { id:"al-02",title:"Camera Offline",       severity:"medium",camera:"CAM-03",time:"09:30 AM",status:"Unresolved" },
    { id:"al-03",title:"Unauthorized Access",  severity:"high",  camera:"CAM-02",time:"Yesterday",status:"Resolved" },
  ],
  report:[
    { id:"rp-1",title:"Daily Summary",  type:"chart",color:"#8b5cf6",period:"Today" },
    { id:"rp-2",title:"Weekly Trend",   type:"chart",color:"#0ea5e9",period:"This Week" },
    { id:"rp-3",title:"Incident Report",type:"table" },
  ],
  safety:[
    { id:"sf-1",title:"PPE Compliance Rate",type:"stat", value:"91%",delta:"+2%",up:true, icon:<HardHat size={16}/>,       color:"#22c55e" },
    { id:"sf-2",title:"Violations Today",   type:"stat", value:"5",  delta:"+1", up:false,icon:<AlertTriangle size={16}/>,color:"#ef4444" },
    { id:"sf-3",title:"PPE Trend",          type:"chart",color:"#10b981" },
    { id:"sf-4",title:"Zone Violations",    type:"chart",color:"#f59e0b" },
  ],
  heatmap:[
    { id:"hm-1",title:"Morning Heatmap (9–12)",  type:"heatmap",color:"#f97316" },
    { id:"hm-2",title:"Afternoon Heatmap (1–5)", type:"heatmap",color:"#ef4444" },
    { id:"hm-3",title:"Peak Zone Summary",       type:"stat",value:"Zone B",delta:"72% peak",up:null,icon:<Map size={16}/>,color:"#f97316" },
  ],
  people:[
    { id:"pp-1",title:"Staff On Floor",     type:"stat", value:"12", delta:"+2", up:true, icon:<Users size={16}/>,    color:"#6366f1" },
    { id:"pp-2",title:"Avg Productivity",   type:"stat", value:"84%",delta:"-1%",up:false,icon:<TrendingUp size={16}/>,color:"#0ea5e9" },
    { id:"pp-3",title:"Attendance Timeline",type:"chart",color:"#8b5cf6" },
    { id:"pp-4",title:"Staff Movement Log", type:"list" },
  ],
  inventory:[
    { id:"in-1",title:"Shelf Fill Rate", type:"stat", value:"78%",delta:"-3%",up:false,icon:<Package size={16}/>,       color:"#f59e0b" },
    { id:"in-2",title:"Low Stock Items", type:"stat", value:"7",  delta:"+2", up:false,icon:<AlertTriangle size={16}/>, color:"#ef4444" },
    { id:"in-3",title:"Restock Events",  type:"chart",color:"#10b981" },
    { id:"in-4",title:"Stock Status",    type:"table" },
  ],
  production:[
    { id:"pd-1",title:"Line Speed",       type:"stat", value:"92 u/h",delta:"+4", up:true, icon:<Zap size={16}/>,    color:"#22c55e" },
    { id:"pd-2",title:"Defects Detected", type:"stat", value:"11",    delta:"-3", up:true, icon:<XCircle size={16}/>,color:"#ef4444" },
    { id:"pd-3",title:"Production Trend", type:"chart",color:"#3b82f6" },
    { id:"pd-4",title:"Defect Breakdown", type:"chart",color:"#f59e0b" },
  ],
};

// ─── FIX: resolveWidgets now accepts plain string[] useCases only ─────────────
function resolveWidgets(pageKey, useCases) {
  if (!useCases || useCases.length === 0) {
    return FALLBACK_WIDGETS[pageKey] || [];
  }

  const widgetKeyMapCI = {};
  Object.keys(USE_CASE_WIDGETS).forEach(k => {
    widgetKeyMapCI[k.toLowerCase().trim()] = k;
  });

  const widgets = [];
  const seen = new Set();

  useCases.forEach((uc) => {
    if (typeof uc !== "string") return;
    const normalized = uc.toLowerCase().trim();
    const fromKeyMap = USE_CASE_KEY_MAP[normalized];
    const widgetKey = (fromKeyMap && USE_CASE_WIDGETS[fromKeyMap])
      ? fromKeyMap
      : widgetKeyMapCI[normalized];

    if (!widgetKey) return;
    const pageWidgets = USE_CASE_WIDGETS[widgetKey]?.[pageKey];
    if (!pageWidgets) return;

    pageWidgets.forEach(w => {
      if (!seen.has(w.id)) {
        seen.add(w.id);
        widgets.push(w);
      }
    });
  });

  return widgets.length ? widgets : (FALLBACK_WIDGETS[pageKey] || []);
}

function shouldShowConfigPrompt(useCases) {
  if (!useCases || useCases.length === 0) return false;
  return useCases.some((uc) => {
    if (typeof uc !== "string") return false;
    const normalized = uc.toLowerCase().trim();
    return (
      USE_CASE_KEY_MAP[normalized] !== undefined ||
      Object.keys(USE_CASE_WIDGETS).some(k => k.toLowerCase() === normalized)
    );
  });
}

// ─── REALTIME DATA HOOK ───────────────────────────────────────────────────────
function useRealtimeData(color, length = 20) {
  const seed = color.charCodeAt(1) + color.charCodeAt(2);
  const init = Array.from({ length }, (_, i) => ({
    t: i,
    v: 30 + 40 * Math.abs(Math.sin((i + seed) * 0.4)),
  }));
  const [data, setData] = useState(init);
  const counter = useRef(length);

  useEffect(() => {
    const id = setInterval(() => {
      setData(prev => {
        const last = prev[prev.length - 1].v;
        const next = Math.max(5, Math.min(95, last + (Math.random() - 0.48) * 14));
        return [...prev.slice(1), { t: counter.current++, v: next }];
      });
    }, 1200);
    return () => clearInterval(id);
  }, []);

  return data;
}

function RealtimeLineChart({ color = "#6366f1", title, period }) {
  const data = useRealtimeData(color);
  const svgRef = useRef(null);
  const W = 560, H = 140, pad = { top: 12, right: 8, bottom: 28, left: 36 };
  const iW = W - pad.left - pad.right;
  const iH = H - pad.top - pad.bottom;

  const minV = Math.min(...data.map(d => d.v));
  const maxV = Math.max(...data.map(d => d.v));
  const range = maxV - minV || 1;

  const xScale = i => pad.left + (i / (data.length - 1)) * iW;
  const yScale = v => pad.top + iH - ((v - minV) / range) * iH;

  const linePath = data.map((d, i) => `${i === 0 ? "M" : "L"}${xScale(i).toFixed(1)},${yScale(d.v).toFixed(1)}`).join(" ");
  const areaPath = linePath + ` L${xScale(data.length - 1).toFixed(1)},${(pad.top + iH).toFixed(1)} L${pad.left},${(pad.top + iH).toFixed(1)} Z`;

  const gradId = `rtg-${color.replace(/[^a-z0-9]/gi, "")}`;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(f => minV + f * range);
  const last = data[data.length - 1];

  return (
    <div style={{ width: "100%", position: "relative" }}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {ticks.map((tick, i) => (
          <g key={i}>
            <line x1={pad.left} y1={yScale(tick).toFixed(1)} x2={pad.left + iW} y2={yScale(tick).toFixed(1)} stroke="#f1f5f9" strokeWidth="1"/>
            <text x={pad.left - 6} y={yScale(tick) + 4} textAnchor="end" fontSize="9" fill="#cbd5e1" fontFamily="'DM Sans',sans-serif">{tick.toFixed(0)}</text>
          </g>
        ))}
        <path d={areaPath} fill={`url(#${gradId})`} />
        <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={xScale(data.length - 1).toFixed(1)} cy={yScale(last.v).toFixed(1)} r="4" fill={color}/>
        <circle cx={xScale(data.length - 1).toFixed(1)} cy={yScale(last.v).toFixed(1)} r="7" fill={color} opacity="0.18">
          <animate attributeName="r" values="4;10;4" dur="2s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.18;0;0.18" dur="2s" repeatCount="indefinite"/>
        </circle>
        {[0, 4, 9, 14, 19].map(i => (
          <text key={i} x={xScale(i).toFixed(1)} y={H - 6} textAnchor="middle" fontSize="9" fill="#cbd5e1" fontFamily="'DM Sans',sans-serif">-{(data.length - 1 - i) * 1.2 | 0}s</text>
        ))}
        <rect x={xScale(data.length - 1) - 20} y={yScale(last.v) - 22} width="40" height="14" rx="7" fill={color}/>
        <text x={xScale(data.length - 1)} y={yScale(last.v) - 12} textAnchor="middle" fontSize="9" fill="white" fontWeight="700" fontFamily="'DM Sans',sans-serif">{last.v.toFixed(1)}</text>
      </svg>
    </div>
  );
}

function HeatmapGrid({ color }) {
  const rows = 6, cols = 10;
  const seed = color.charCodeAt(1);
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols},1fr)`, gap: "3px" }}>
      {Array.from({ length: rows * cols }).map((_, i) => {
        const intensity = Math.abs(Math.sin((i * 7 + seed) * 0.37));
        return <div key={i} style={{ aspectRatio: "1", borderRadius: "3px", background: color, opacity: 0.08 + intensity * 0.88, transition: "opacity 0.6s ease" }}/>;
      })}
    </div>
  );
}

function GaugeChart({ value, color }) {
  const pct = (value || 0) / 100;
  const r = 42, cx = 60, cy = 60;
  const circ = 2 * Math.PI * r;
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "8px 0" }}>
      <svg viewBox="0 0 120 120" style={{ width: 120, height: 120 }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f1f5f9" strokeWidth="10"/>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
          strokeLinecap="round" transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition: "stroke-dashoffset 1.2s ease" }}/>
        <text x="60" y="56" textAnchor="middle" fontSize="20" fontWeight="800" fontFamily="'Sora',sans-serif" fill="#0f172a">{value}%</text>
        <text x="60" y="72" textAnchor="middle" fontSize="9" fill="#94a3b8" fontFamily="'DM Sans',sans-serif">SCORE</text>
      </svg>
    </div>
  );
}

function StatCard({ widget }) {
  const isNull = widget.up === null;
  return (
    <div style={{ background: "#fff", borderRadius: 16, padding: "20px 20px 16px", border: "1px solid #f1f5f9", display: "flex", flexDirection: "column", gap: 14, boxShadow: "0 1px 4px rgba(15,23,42,0.04)", transition: "box-shadow 0.2s, border-color 0.2s" }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(99,102,241,0.10)"; e.currentTarget.style.borderColor = "#e0e7ff"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(15,23,42,0.04)"; e.currentTarget.style.borderColor = "#f1f5f9"; }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: widget.color + "14", color: widget.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{widget.icon}</div>
          <span style={{ fontSize: 12, fontWeight: 500, color: "#64748b" }}>{widget.title}</span>
        </div>
        {!isNull ? (
          <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 999, background: widget.up ? "#f0fdf4" : "#fef2f2", color: widget.up ? "#16a34a" : "#dc2626", display: "flex", alignItems: "center", gap: 2 }}>
            {widget.up ? <ArrowUpRight size={9}/> : <ArrowDownRight size={9}/>}{widget.delta}
          </span>
        ) : (
          <span style={{ fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 999, background: "#f8fafc", color: "#64748b" }}>{widget.delta}</span>
        )}
      </div>
      <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 32, fontWeight: 800, letterSpacing: "-1.5px", color: "#0f172a", lineHeight: 1 }}>{widget.value}</div>
    </div>
  );
}

function ChartCard({ widget }) {
  return (
    <div style={{ background: "#fff", borderRadius: 16, padding: "20px 20px 14px", border: "1px solid #f1f5f9", display: "flex", flexDirection: "column", gap: 12, boxShadow: "0 1px 4px rgba(15,23,42,0.04)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", letterSpacing: "-0.2px", fontFamily: "'Sora',sans-serif" }}>{widget.title}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 6, height: 6, borderRadius: 3, background: "#22c55e" }}/>
          <span style={{ fontSize: 10, fontWeight: 600, color: "#22c55e", letterSpacing: 0.5 }}>LIVE</span>
          <span style={{ fontSize: 10, fontWeight: 500, color: "#94a3b8", background: "#f8fafc", padding: "2px 8px", borderRadius: 999, marginLeft: 4 }}>{widget.period || "Real-time"}</span>
        </div>
      </div>
      <RealtimeLineChart color={widget.color} title={widget.title} period={widget.period}/>
    </div>
  );
}

function HeatmapCard({ widget }) {
  return (
    <div style={{ background: "#fff", borderRadius: 16, padding: "20px 20px 16px", border: "1px solid #f1f5f9", display: "flex", flexDirection: "column", gap: 12, boxShadow: "0 1px 4px rgba(15,23,42,0.04)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", fontFamily: "'Sora',sans-serif" }}>{widget.title}</span>
        <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", color: "#fff", background: widget.color, padding: "3px 10px", borderRadius: 999 }}>Heatmap</span>
      </div>
      <HeatmapGrid color={widget.color}/>
      <div style={{ display: "flex", gap: 14, marginTop: 4 }}>
        {["Low", "Medium", "High"].map((l, i) => (
          <div key={l} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: widget.color, opacity: 0.15 + i * 0.4 }}/>
            <span style={{ fontSize: 10, color: "#94a3b8" }}>{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function GaugeCard({ widget }) {
  return (
    <div style={{ background: "#fff", borderRadius: 16, padding: "20px 20px 16px", border: "1px solid #f1f5f9", display: "flex", flexDirection: "column", gap: 4, boxShadow: "0 1px 4px rgba(15,23,42,0.04)" }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", fontFamily: "'Sora',sans-serif" }}>{widget.title}</span>
      <GaugeChart value={widget.value} color={widget.color}/>
      <p style={{ fontSize: 11, color: "#94a3b8", textAlign: "center", margin: 0 }}>{widget.title}</p>
    </div>
  );
}

function ListCard({ widget }) {
  const items = [
    { label: "Motion detected — Zone A",       time: "10:42 AM", dot: "#ef4444" },
    { label: "Staff check-in — Counter",        time: "10:38 AM", dot: "#22c55e" },
    { label: "Queue alert cleared",             time: "10:31 AM", dot: "#f59e0b" },
    { label: "Camera CAM-03 reconnected",       time: "10:20 AM", dot: "#3b82f6" },
  ];
  return (
    <div style={{ background: "#fff", borderRadius: 16, padding: "20px 20px 8px", border: "1px solid #f1f5f9", display: "flex", flexDirection: "column", gap: 4, boxShadow: "0 1px 4px rgba(15,23,42,0.04)" }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", fontFamily: "'Sora',sans-serif", marginBottom: 8 }}>{widget.title}</span>
      {items.map((it, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: i < items.length - 1 ? "1px solid #f8fafc" : "none" }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: it.dot, flexShrink: 0 }}/>
          <span style={{ flex: 1, fontSize: 12.5, color: "#475569" }}>{it.label}</span>
          <span style={{ fontSize: 11, color: "#94a3b8", fontVariantNumeric: "tabular-nums" }}>{it.time}</span>
        </div>
      ))}
    </div>
  );
}

function TableCard({ widget }) {
  const rows = [
    { id: "EVT-001", desc: "Crowd Limit Exceeded", cam: "CAM-01", time: "10:42", status: "Open",     color: "#ef4444" },
    { id: "EVT-002", desc: "Camera Offline",        cam: "CAM-03", time: "09:30", status: "Pending",  color: "#f59e0b" },
    { id: "EVT-003", desc: "Unauthorised Access",   cam: "CAM-02", time: "08:15", status: "Resolved", color: "#22c55e" },
  ];
  return (
    <div style={{ background: "#fff", borderRadius: 16, padding: "20px", border: "1px solid #f1f5f9", boxShadow: "0 1px 4px rgba(15,23,42,0.04)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", fontFamily: "'Sora',sans-serif" }}>{widget.title}</span>
        <button style={{ fontSize: 11, fontWeight: 600, color: "#6366f1", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 3 }}>View all <ChevronRight size={11}/></button>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#f8fafc" }}>
            {["ID","Description","Camera","Time","Status"].map(h => (
              <th key={h} style={{ fontSize: 10, fontWeight: 700, color: "#64748b", letterSpacing: 0.6, textTransform: "uppercase", padding: "8px 10px", textAlign: "left" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.id} style={{ borderBottom: "1px solid #f8fafc" }}>
              <td style={{ padding: "10px", fontSize: 10.5, color: "#94a3b8", fontFamily: "monospace", letterSpacing: 0.5 }}>{row.id}</td>
              <td style={{ padding: "10px", fontSize: 13, fontWeight: 500, color: "#334155" }}>{row.desc}</td>
              <td style={{ padding: "10px", fontSize: 12, color: "#64748b" }}>{row.cam}</td>
              <td style={{ padding: "10px", fontSize: 12, color: "#64748b" }}>{row.time}</td>
              <td style={{ padding: "10px" }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 999, background: row.color + "14", color: row.color }}>{row.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FeedCard({ widget }) {
  const online = widget.status === "online";
  const healthColor = { Good: "#22c55e", Average: "#f59e0b", Critical: "#ef4444" }[widget.health];
  return (
    <div style={{ background: "#fff", borderRadius: 16, border: online ? "1px solid #f1f5f9" : "1px solid #fee2e2", overflow: "hidden", boxShadow: "0 1px 4px rgba(15,23,42,0.04)" }}>
      <div style={{ height: 128, background: online ? "#0f172a" : "#1a0505", position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {online ? (
          <>
            <div style={{ position: "absolute", inset: 0, opacity: 0.08, backgroundImage: "repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(255,255,255,0.06) 2px,rgba(255,255,255,0.06) 4px)" }}/>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, opacity: 0.3 }}>
              <Video size={26} color="#fff"/>
              <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: 3, color: "#fff", textTransform: "uppercase" }}>LIVE</span>
            </div>
            <div style={{ position: "absolute", top: 10, left: 12, display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 6, height: 6, borderRadius: 3, background: "#ef4444" }}/>
              <span style={{ fontSize: 8, fontWeight: 700, color: "#f87171", letterSpacing: 2 }}>REC</span>
            </div>
            <div style={{ position: "absolute", top: 10, right: 12 }}>
              <span style={{ fontSize: 9, color: "#64748b", fontVariantNumeric: "tabular-nums" }}>{new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          </>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, opacity: 0.5 }}>
            <VideoOff size={26} color="#ef4444"/>
            <span style={{ fontSize: 8, fontWeight: 700, color: "#f87171", letterSpacing: 2, textTransform: "uppercase" }}>OFFLINE</span>
          </div>
        )}
      </div>
      <div style={{ padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "#1e293b" }}>{widget.title}</div>
          <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 2 }}>{widget.last}</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {online ? <Wifi size={12} color="#22c55e"/> : <WifiOff size={12} color="#ef4444"/>}
            <span style={{ fontSize: 11, fontWeight: 600, color: online ? "#22c55e" : "#ef4444" }}>{online ? "Online" : "Offline"}</span>
          </div>
          <span style={{ fontSize: 9.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: healthColor + "14", color: healthColor }}>{widget.health}</span>
        </div>
      </div>
    </div>
  );
}

function useContainerWidth(ref) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    // Set immediately on mount
    setWidth(ref.current.getBoundingClientRect().width || ref.current.offsetWidth);
    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        const w = e.contentRect.width;
        if (w > 0) setWidth(w);
      }
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return width;
}

function SectionLabel({ children }) {
  return (
    <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: "#94a3b8", fontFamily: "'DM Sans',sans-serif", whiteSpace: "nowrap" }}>{children}</span>
      <div style={{ flex: 1, height: 1, background: "#f1f5f9" }}/>
    </div>
  );
}

// ─── FIX: GenericPage and AlertsPage now receive plain string[] useCases ──────
function GenericPage({ pageKey, useCases, navigate }) {
  const containerRef = useRef(null);
  const cw = useContainerWidth(containerRef);

  const widgets = resolveWidgets(pageKey, useCases);
  const stats   = widgets.filter(w => w.type === "stat");
  const charts  = widgets.filter(w => w.type === "chart");
  const others  = widgets.filter(w => !["stat","chart"].includes(w.type));

  // Use safe fallback column counts — don't rely on cw===0 state
  const safeCw = cw > 0 ? cw : 1200;
  const statCols    = safeCw < 480 ? 1 : safeCw < 720 ? 2 : safeCw < 1060 ? 3 : 4;
  const chartCols   = safeCw < 680 ? 1 : 2;
  const insightCols = safeCw < 480 ? 1 : safeCw < 900 ? 2 : 3;

  return (
    <div ref={containerRef} style={{ display: "flex", flexDirection: "column", gap: 28, minHeight: 100 }}>
      {stats.length > 0 && (
        <section>
          <SectionLabel>Key Metrics</SectionLabel>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${statCols}, 1fr)`, gap: 16 }}>
            {stats.map(w => <StatCard key={w.id} widget={w}/>)}
          </div>
        </section>
      )}
      {charts.length > 0 && (
        <section>
          <SectionLabel>Live Analytics</SectionLabel>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${chartCols}, 1fr)`, gap: 16 }}>
            {charts.map(w => <ChartCard key={w.id} widget={w}/>)}
          </div>
        </section>
      )}
      {others.length > 0 && (
        <section>
          <SectionLabel>Insights</SectionLabel>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${insightCols}, 1fr)`, gap: 16 }}>
            {others.map(w => {
              if (w.type === "table")   return <div key={w.id} style={{ gridColumn: "1 / -1" }}><TableCard widget={w}/></div>;
              if (w.type === "heatmap") return <HeatmapCard key={w.id} widget={w}/>;
              if (w.type === "gauge")   return <GaugeCard   key={w.id} widget={w}/>;
              if (w.type === "list")    return <ListCard     key={w.id} widget={w}/>;
              return null;
            })}
          </div>
        </section>
      )}
    </div>
  );
}

function AlertsPage({ useCases, navigate }) {
  const alerts = [], seen = new Set();

  const widgetKeyMapCI = {};
  Object.keys(USE_CASE_WIDGETS).forEach(k => {
    widgetKeyMapCI[k.toLowerCase().trim()] = k;
  });

  for (const uc of useCases) {
    if (typeof uc !== "string") continue;
    const normalized = uc.toLowerCase().trim();
    const fromKeyMap = USE_CASE_KEY_MAP[normalized];
    const widgetKey = (fromKeyMap && USE_CASE_WIDGETS[fromKeyMap])
      ? fromKeyMap
      : widgetKeyMapCI[normalized];

    if (!widgetKey) continue;
    const ucw = USE_CASE_WIDGETS[widgetKey];
    if (!ucw?.alert) continue;
    for (const a of ucw.alert) {
      if (!seen.has(a.id)) { seen.add(a.id); alerts.push(a); }
    }
  }

  const finalAlerts = alerts.length > 0 ? alerts : FALLBACK_WIDGETS.alert;
  const sevColor    = { high: "#ef4444", medium: "#f59e0b", low: "#3b82f6" };
  const statusColor = { Open: "#ef4444", Unresolved: "#f59e0b", Resolved: "#22c55e" };
  const counts      = { Open: 0, Unresolved: 0, Resolved: 0 };
  finalAlerts.forEach(a => { counts[a.status] = (counts[a.status] || 0) + 1; });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
        {Object.entries(counts).map(([label, count]) => (
          <div key={label} style={{ background: "#fff", borderRadius: 14, padding: "16px 18px", border: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 12, boxShadow: "0 1px 4px rgba(15,23,42,0.04)" }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: statusColor[label] + "14", color: statusColor[label], display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 800, fontFamily: "'Sora',sans-serif" }}>{count}</div>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{label}</span>
          </div>
        ))}
      </div>
      <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #f1f5f9", overflow: "hidden", boxShadow: "0 1px 4px rgba(15,23,42,0.04)" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #f8fafc", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1e293b", fontFamily: "'Sora',sans-serif", display: "flex", alignItems: "center", gap: 6 }}>
            <Bell size={14} color="#ef4444"/> System Alerts
          </h3>
          <button style={{ fontSize: 11, fontWeight: 600, color: "#6366f1", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
            Mark all read <CheckCircle2 size={12}/>
          </button>
        </div>
        <div>
          {finalAlerts.map((al, i) => (
            <div key={al.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 20px", borderBottom: i < finalAlerts.length - 1 ? "1px solid #f8fafc" : "none", transition: "background 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: sevColor[al.severity], flexShrink: 0 }}/>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: "#1e293b" }}>{al.title}</div>
                <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 2 }}>{al.camera} · {al.time}</div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 999, background: sevColor[al.severity] + "14", color: sevColor[al.severity] }}>{al.severity.toUpperCase()}</span>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 999, background: statusColor[al.status] + "14", color: statusColor[al.status] }}>{al.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function UseCaseBanner({ useCases, sector }) {
  if (!useCases || useCases.length === 0) return null;
  const sectorColors = {
    "QSR / Fast Food": { bg: "#fff7ed", border: "#fed7aa", text: "#c2410c", dot: "#f97316" },
    "Manufacturing":   { bg: "#fffbeb", border: "#fde68a", text: "#b45309", dot: "#f59e0b" },
    "Retail":          { bg: "#f5f3ff", border: "#ddd6fe", text: "#6d28d9", dot: "#8b5cf6" },
  };
  const sc = sectorColors[sector] || { bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8", dot: "#3b82f6" };
  return (
    <div style={{ marginBottom: 20, padding: "10px 16px", borderRadius: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: sc.bg, border: `1px solid ${sc.border}` }}>
      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: sc.text }}>Active Use Cases</span>
      {useCases.map(uc => (
        <span key={uc} style={{ fontSize: 11.5, fontWeight: 600, padding: "3px 10px", borderRadius: 999, background: sc.dot + "16", color: sc.text, border: `1px solid ${sc.dot}28`, display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: sc.dot }}/>{uc}
        </span>
      ))}
    </div>
  );
}

// ─── MAIN DASHBOARD ──────────────────────────────────────────────────────────
export default function DemoDashboard() {
  const navigate = useNavigate();
  const { dispatch } = useContext(RootContext);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedPage, setSelectedPage] = useState("dashboard");
  const [time, setTime] = useState(new Date());

  const user        = JSON.parse(localStorage.getItem("user") || "{}");
  const orgName     = localStorage.getItem("organizationName") || user?.organizationName || "Your Organisation";
  const sector      = localStorage.getItem("sector") || user?.sector || "";

  const rawUseCases = JSON.parse(localStorage.getItem("selectedUseCases")) || [];

  const useCases = rawUseCases.map((uc) => {
    let rawId = "";

    if (typeof uc === "string") {
      rawId = uc;
    } else if (uc?.id) {
      rawId = uc.id;
    }

    const normalized = rawId
      .replace(/[-_]/g, " ")
      .toLowerCase()
      .trim();

    return (
      USE_CASE_KEY_MAP[normalized] ||
      Object.keys(USE_CASE_WIDGETS).find(k => k.toLowerCase() === normalized) ||
      rawId
        .replace(/[-_]/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase())
    );
  }).filter(Boolean);

  const allowedPages = useCases.length > 0
    ? [...new Set(["dashboard", ...useCases.flatMap(uc => USE_CASE_PAGES[uc] || [])])]
    : Object.keys(PAGE_META);

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const pageMeta = PAGE_META[selectedPage];

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    document.cookie.split(";").forEach(c => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
    dispatch({ type: "LOGOUT" });
    navigate("/loginconfiguration", { replace: true });
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'DM Sans', sans-serif; }
        .dash-root { font-family: 'DM Sans', sans-serif; -webkit-font-smoothing: antialiased; }
        @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(1.5)} }
        @keyframes fadeUp { 
          from { opacity: 0; transform: translateY(10px); } 
          to   { opacity: 1; transform: translateY(0); } 
        }
        .page-enter { 
          animation: fadeUp 0.3s ease both; 
          will-change: opacity, transform;
        }
        .nav-btn { transition: all 0.15s ease; cursor: pointer; }
        .nav-btn:hover { transform: translateX(2px); }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 2px; }
      `}</style>

      <div className="dash-root" style={{ display: "flex", height: "100vh", background: "#f8fafc", overflow: "hidden" }}>

        {/* ── SIDEBAR ── */}
        <aside style={{ flexShrink: 0, width: sidebarOpen ? 240 : 60, background: "#fff", borderRight: "1px solid #f1f5f9", display: "flex", flexDirection: "column", transition: "width 0.28s cubic-bezier(0.4,0,0.2,1)", overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: sidebarOpen ? "18px 16px" : "18px 12px", borderBottom: "1px solid #f8fafc", justifyContent: sidebarOpen ? "flex-start" : "center" }}>
            <div style={{ width: 32, height: 32, borderRadius: 10, flexShrink: 0, background: "linear-gradient(135deg,#6366f1,#4f46e5)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 14 }}>C</div>
            {sidebarOpen && (
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 14, fontWeight: 700, letterSpacing: "-0.3px", color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Citriot</div>
                <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{orgName}</div>
              </div>
            )}
          </div>

          <nav style={{ flex: 1, padding: "12px 8px", display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}>
            {allowedPages.map(key => {
              const meta = PAGE_META[key];
              if (!meta) return null;
              const active = selectedPage === key;
              return (
                <div key={key} className="nav-btn" onClick={() => setSelectedPage(key)}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: sidebarOpen ? "9px 12px" : "10px", borderRadius: 10, justifyContent: sidebarOpen ? "flex-start" : "center", background: active ? "#eef2ff" : "transparent", color: active ? "#6366f1" : "#64748b" }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = "#f8fafc"; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}
                >
                  <span style={{ flexShrink: 0 }}>{meta.icon}</span>
                  {sidebarOpen && (
                    <>
                      <span style={{ fontSize: 13, fontWeight: active ? 600 : 400, letterSpacing: "-0.1px", whiteSpace: "nowrap" }}>{meta.title}</span>
                      {active && <div style={{ marginLeft: "auto", width: 6, height: 6, borderRadius: 3, background: "#6366f1" }}/>}
                    </>
                  )}
                </div>
              );
            })}
          </nav>

          <div style={{ padding: "10px 8px", borderTop: "1px solid #f1f5f9" }}>
            <div
              className="nav-btn"
              onClick={handleLogout}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: sidebarOpen ? "9px 12px" : "10px", borderRadius: 10, justifyContent: sidebarOpen ? "flex-start" : "center", background: "transparent", color: "#ef4444", cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.background = "#fef2f2"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              title="Logout"
            >
              <span style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </span>
              {sidebarOpen && (
                <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: "-0.1px", whiteSpace: "nowrap" }}>Logout</span>
              )}
            </div>
          </div>
        </aside>

        {/* ── MAIN ── */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <header style={{ flexShrink: 0, background: "#fff", borderBottom: "1px solid #f1f5f9", padding: "0 24px", height: 60, display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 1px 0 #f1f5f9" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <button onClick={() => setSidebarOpen(v => !v)} style={{ width: 34, height: 34, borderRadius: 9, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", transition: "background 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                {sidebarOpen ? <X size={17}/> : <Menu size={17}/>}
              </button>
              <div>
                <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: 17, fontWeight: 700, letterSpacing: "-0.5px", color: "#0f172a", display: "flex", alignItems: "center", gap: 6 }}>{pageMeta.icon}{pageMeta.title}</h1>
                <p style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 1 }}>{pageMeta.description}</p>
              </div>
            </div>

            {shouldShowConfigPrompt(useCases) && (
              <div style={{ flex: 1, display: "flex", justifyContent: "center", padding: "0 16px" }}>
                <div style={{ background: "#f8fafc", border: "1px solid #e0e7ff", borderRadius: 12, padding: "6px 14px", display: "flex", alignItems: "center", gap: 10, maxWidth: 480, width: "100%" }}>
                  <div style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, background: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Settings size={13} color="#6366f1"/>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1e293b", fontFamily: "'Sora',sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Go live with Dashboard Settings</div>
                    <div style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Set up zones and alert thresholds to go live</div>
                  </div>
                  <button onClick={handleLogout} style={{ flexShrink: 0, background: "#eef2ff", color: "#6366f1", border: "1px solid #c7d2fe", borderRadius: 8, padding: "5px 12px", fontSize: 12, fontWeight: 700, fontFamily: "'Sora',sans-serif", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
                    <Settings size={12}/> Login to Configure
                  </button>
                </div>
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {useCases.slice(0, 2).map(uc => (
                <span key={uc} style={{ fontSize: 10, fontWeight: 600, padding: "4px 10px", borderRadius: 999, background: "#eef2ff", color: "#6366f1", border: "1px solid #e0e7ff", display: "flex", alignItems: "center", gap: 4 }}>
                  <Star size={8}/> {uc}
                </span>
              ))}
              {useCases.length > 2 && (
                <span style={{ fontSize: 10, fontWeight: 500, padding: "4px 10px", borderRadius: 999, background: "#f8fafc", color: "#94a3b8" }}>+{useCases.length - 2}</span>
              )}
              <span style={{ fontSize: 11.5, fontWeight: 500, color: "#94a3b8", fontVariantNumeric: "tabular-nums", marginLeft: 4 }}>{time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
              <div style={{ width: 34, height: 34, borderRadius: 10, flexShrink: 0, background: "linear-gradient(135deg,#6366f1,#4f46e5)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 700, fontFamily: "'Sora',sans-serif" }}>
                {(user?.name || user?.username || "U")[0].toUpperCase()}
              </div>
            </div>
          </header>

          <main style={{ flex: 1, overflowY: "auto", padding: 24 }}>
            {/* ── FIX: pass useCases (plain string[]) directly — NO <div> wrappers ── */}
            <div className="page-enter" key={selectedPage}>
              <UseCaseBanner useCases={useCases} sector={sector}/>
              {selectedPage === "alert" ? (
                <AlertsPage useCases={useCases} navigate={navigate}/>
              ) : selectedPage === "camera" ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 16 }}>
                  {resolveWidgets("camera", useCases).map(w => <FeedCard key={w.id} widget={w}/>)}
                </div>
              ) : (
                <GenericPage pageKey={selectedPage} useCases={useCases} navigate={navigate}/>
              )}
            </div>
          </main>
        </div>
      </div>
    </>
  );
}